import type { TFunction } from 'i18next'

import type { PortalContext } from '@/hooks/usePortalMe.types'

import { cleDuTypeDEspace, ligneDeuxDuContexte } from './contextKind'

/**
 * PORT-005 §5.2 (arbitrage PO n° 11) — **l'ordre dans lequel une personne voit
 * ses espaces.** UNE fonction, lue par la page de choix, le sélecteur de l'en-tête
 * et la carte « Espace par défaut » : écrits séparément, deux tris divergent au
 * premier ajout, et l'espace qu'on a choisi en haut d'une liste se retrouve
 * ailleurs dans l'autre.
 *
 * ① l'espace ACTIF en tête ;
 * ② `last_access_at` décroissant — les jamais-visités en dernier ;
 * ③ la ligne 1 TRADUITE, celle qui est affichée ;
 * ④ la ligne 2 affichée (régime compris), dans l'ordre alphabétique de la langue
 *   de l'écran — `Intl.Collator` : « Émeraude » vient après « Delta », pas après « Z » ;
 * ⑤ l'`id`, pour qu'aucune égalité ne dépende de l'ordre reçu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ POURQUOI CE TRI N'EST PAS AU SERVEUR — et ne doit pas y remonter
 * ═══════════════════════════════════════════════════════════════════════════
 * `list_contexts_for_user` ne connaît ni l'espace actif (l'acteur le calcule après
 * l'appel) ni la langue de l'écran : trié au serveur, ③ serait le `title` français.
 * Et l'ordre du serveur a un SECOND lecteur, `api/portal/_nom_de_la_personne.py`,
 * qui retient le premier nom de fiche rencontré : trier là-bas par dernière visite
 * ferait changer « Bonjour … » selon l'espace visité la veille. Le serveur garde
 * son ordre déterministe `(kind, title, id)` ; l'écran ordonne ce qu'il affiche.
 */
export function ordonnerLesEspaces(
    contextes: readonly PortalContext[],
    actifId: string | null,
    t: TFunction,
    langue: string,
): PortalContext[] {
    let collator: Intl.Collator
    try {
        collator = new Intl.Collator(langue)
    } catch {
        // Une étiquette de langue invalide lève une `RangeError` : on trie alors
        // dans la langue par défaut du navigateur plutôt que de ne rien afficher.
        collator = new Intl.Collator()
    }
    const cle = (c: PortalContext) => ({
        actif: c.id === actifId ? 0 : 1,
        // Jamais visité — ou date illisible : `NaN` rendrait la comparaison incohérente.
        visite: Number.isFinite(Date.parse(c.last_access_at ?? '')) ? Date.parse(c.last_access_at!) : Number.NEGATIVE_INFINITY,
        ligne1: t(cleDuTypeDEspace(c.kind)),
        ligne2: ligneDeuxDuContexte(c, t),
    })
    return contextes
        .map((c) => ({ c, k: cle(c) }))
        .sort((a, b) =>
            a.k.actif - b.k.actif
            || (b.k.visite === a.k.visite ? 0 : b.k.visite > a.k.visite ? 1 : -1)
            || collator.compare(a.k.ligne1, b.k.ligne1)
            || collator.compare(a.k.ligne2, b.k.ligne2)
            || (a.c.id < b.c.id ? -1 : a.c.id > b.c.id ? 1 : 0))
        .map(({ c }) => c)
}
